import {
  Collapse,
  Empty,
  Form,
  type FormListFieldData,
  type FormListOperation,
  Table
} from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React, {
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react"

import { type DocumentScopeSectionProjectTypes } from "../../../../types/document.types.ts"
import type {
  TemplateDocumentFormData,
  TemplateDocumentScopeSectionPhase
} from "../../../../types/template-document-form-data.tsx"
import { ScopeOfWorkBlockAddMilestone } from "./ScopeOfWorkBlockAddMilestone.tsx"
import { getDocumentScopeSectionColumnsColumns } from "./ScopeOfWorkBlockColumns.tsx"
import { ScopeOfWorkBlockFooter } from "./ScopeOfWorkBlockFooter.tsx"

export interface DocumentScopeSectionListRef {
  setCollapsedActiveKey: React.Dispatch<React.SetStateAction<string[]>>
}

interface ScopeOfWorkBlockListProps {
  form: FormInstance<TemplateDocumentFormData>
  index: number
  fields: FormListFieldData[]
  columnKey: Extract<keyof TemplateDocumentFormData, "phases">
  operations: FormListOperation
  disabled?: boolean
  projectTypes: DocumentScopeSectionProjectTypes
  listRef: Ref<DocumentScopeSectionListRef>
}

export const ScopeOfWorkBlockList = ({
  form,
  index,
  fields,
  columnKey,
  disabled,
  listRef,
  projectTypes,
  operations: { add, remove }
}: ScopeOfWorkBlockListProps) => {
  const typeOrderRef = useRef<string[]>([])
  const [activeKey, setActiveKey] = useState<string[]>([])

  const hiddenColumns =
    Form.useWatch(["blocks", index, "config", "hidden_columns"], form) || []

  const onAddMilestone = (
    defaultValue?: Partial<TemplateDocumentScopeSectionPhase>,
    insertIndex?: number
  ) => {
    add(defaultValue, insertIndex)

    const newIndex = insertIndex ?? fields.length
    const projectType = defaultValue?.project_type

    if (projectType && !activeKey?.includes(projectType)) {
      setActiveKey((prev) => [...(prev || []), projectType])
    }

    setTimeout(() => {
      form.focusField([
        columnKey,
        newIndex,
        "name" as keyof TemplateDocumentScopeSectionPhase
      ])
    }, 500)
  }

  const groupedFields = useMemo(() => {
    const phases = form.getFieldValue(columnKey) || []
    const groupMap = new Map<
      string,
      {
        projectType: string
        projectTypeName: string
        fields: FormListFieldData[]
      }
    >()

    fields.forEach((field) => {
      const phase = phases[field.name]
      if (!phase) return

      const typeId = phase?.project_type_id || phase?.project_type || "unknown"
      const typeName = phase?.project_type_name || "Other"

      if (!groupMap.has(typeId)) {
        groupMap.set(typeId, {
          projectType: typeId,
          projectTypeName: typeName,
          fields: []
        })
      }
      groupMap.get(typeId)!.fields.push(field)
    })

    const kept = typeOrderRef?.current?.filter((id) => groupMap.has(id))
    const newIds = [...groupMap.keys()].filter((id) => !kept.includes(id))
    const newOrder = [...kept, ...newIds]

    return { groupMap, order: newOrder }
  }, [fields, form, columnKey])

  const groups = groupedFields.order.map(
    (id) => groupedFields.groupMap.get(id)!
  )

  const columns = useMemo(
    () =>
      getDocumentScopeSectionColumnsColumns({
        form,
        hiddenColumns,
        disabled,
        onRemove: remove
      }),
    [disabled, form, hiddenColumns, remove]
  )

  useEffect(() => {
    typeOrderRef.current = groupedFields.order
  }, [groupedFields.order])

  useImperativeHandle<DocumentScopeSectionListRef, DocumentScopeSectionListRef>(
    listRef,
    () => ({
      setCollapsedActiveKey: setActiveKey
    })
  )

  return (
    <>
      {groups?.length ? (
        <Collapse
          activeKey={activeKey}
          onChange={setActiveKey}
          items={groups.map((group) => ({
            key: group.projectType,
            label: (
              <span className="font-semibold">
                {group.projectTypeName} ({group.fields.length})
              </span>
            ),
            children: (
              <Table
                pagination={false}
                dataSource={group?.fields}
                scroll={{ x: "max-content" }}
                rowKey="key"
                bordered
                columns={columns}
              />
            )
          }))}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No milestones"
        />
      )}
      <ScopeOfWorkBlockFooter form={form} />

      <ScopeOfWorkBlockAddMilestone
        disabled={disabled}
        onAddMilestone={onAddMilestone}
        fields={fields}
        projectTypes={projectTypes}
      />
    </>
  )
}
